// frontend/client/src/store/useMinoStore.js
//
// Global Mino state. Replaces window.triggerMino with a proper store so:
// - any component can trigger Mino without prop-drilling
// - rapid-fire triggers queue instead of clobbering each other
// - state is inspectable (React DevTools works fine with Zustand)
//
// Install: npm install zustand

import { create } from 'zustand';

const AUTO_HIDE_MS = {
  success: 4000,
  error: 6000,   // errors stay up longer — user needs time to read them
  thinking: null, // thinking never auto-hides; something else must resolve it
  idle: null,
};

let hideTimer = null;

export const useMinoStore = create((set, get) => ({
  status: 'idle',       // 'idle' | 'thinking' | 'success' | 'error'
  message: '',
  subMessage: '',
  queue: [],             // pending triggers if one is already showing

  trigger: (status, message, subMessage = '') => {
    const current = get().status;

    // If Mino is mid-message and not idle, queue this one instead of
    // stomping it — prevents two triggers firing close together from
    // fighting over the bubble.
    if (current !== 'idle' && current !== status) {
      set((state) => ({
        queue: [...state.queue, { status, message, subMessage }],
      }));
      return;
    }

    if (hideTimer) clearTimeout(hideTimer);
    set({ status, message, subMessage });

    const hideAfter = AUTO_HIDE_MS[status];
    if (hideAfter) {
      hideTimer = setTimeout(() => get()._advanceQueue(), hideAfter);
    }
  },

  // Internal: called after an auto-hide timer fires
  _advanceQueue: () => {
    const { queue } = get();
    if (queue.length === 0) {
      set({ status: 'idle', message: '', subMessage: '' });
      return;
    }
    const [next, ...rest] = queue;
    set({ queue: rest });
    get().trigger(next.status, next.message, next.subMessage);
  },

  // Manual dismiss (e.g. user clicks the bubble away)
  dismiss: () => {
    if (hideTimer) clearTimeout(hideTimer);
    set({ status: 'idle', message: '', subMessage: '' });
  },
}));